<script setup lang="ts">
/**
 * The daily-quest card on the garage floor.
 *
 * ── What it says, and what it deliberately does not ──────────────────────────
 *
 * Four things: how much of the day is done, how long the set has left, which
 * quest is worth going back out for, and what that one pays. It is still one
 * *button* — the whole card opens `DailyQuestsWindow`, which is where a quest is
 * read in full and where a reward is claimed. Nothing here claims anything, so
 * there is no control inside a control and no second place for the claim rule to
 * be enforced.
 *
 * It replaced a one-line bookmark that named a quest and carried a count. The
 * count answered *is there something waiting*; what it could not answer is the
 * question a player actually has in the garage — *is it worth one more sortie* —
 * which needs the progress, the remaining time and the payout. All three are on
 * the overview already, so the card costs no request.
 *
 * ── Every figure is derived, and none of it is invented here ─────────────────
 *
 * The wording, the targets and the rewards are the server's, exactly as the
 * window has them: this file holds no copy of the quest pool. The arithmetic —
 * which quest is featured, how full each segment is, what the clock reads — is
 * `daily-quest-box.ts`, pure and tested, because all three are wrong in ways no
 * screenshot shows.
 *
 * ── The clock is a subtraction, and the ticker only makes it move ───────────
 *
 * `secondsUntilReset` is a remainder measured at the instant of the response, so
 * the countdown is measured against `loadedAtMs` rather than accumulated — a tab
 * suspended for an hour comes back with the right remainder instead of an hour of
 * missed intervals. Nothing counts down; the interval exists so the text
 * re-renders. On the boundary the card **asks** rather than concluding a new day
 * has begun: whether the day has changed is the server's answer, and it is the
 * same `elapsed` seam the window uses.
 *
 * ── The glyphs are drawn, not fetched ───────────────────────────────────────
 *
 * One per metric, in CSS-pixel SVG at 14 px, for the reason the radar bezel and
 * the title badges are drawn rather than rastered: the HUD and the garage render
 * at `devicePixelRatio`, so any fixed image is soft on half the machines that run
 * this game. An unrecognised metric falls back to the ring rather than to nothing
 * — a card with a hole in it is worse than a card with a generic mark.
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import type { DailyQuests } from '@/net/api';
import CurrencyIcon from '@/ui/CurrencyIcon.vue';
import {
  completedCount,
  countdownLabel,
  featuredQuest,
  featuredState,
  questSegments,
  secondsUntilReset,
} from './daily-quest-box';
import { formatCredits } from './stat-display';

const props = defineProps<{
  quests: DailyQuests;
  /** When the overview these figures came from arrived. The countdown measures from here. */
  loadedAtMs: number;
  /** The player's diamonds, to disable a re-roll they cannot pay for. */
  diamonds?: number;
}>();

const emit = defineEmits<{
  open: [];
  /** The small re-roll button; the screen asks before it spends. */
  reroll: [];
  /** The day rolled over. Re-reading belongs to whoever owns the store. */
  elapsed: [];
}>();

const nowMs = ref(Date.now());
let ticker: ReturnType<typeof setInterval> | null = null;
let announcedElapsed = false;

const secondsLeft = computed(() =>
  secondsUntilReset(props.quests, props.loadedAtMs, nowMs.value),
);
const resetLabel = computed(() => countdownLabel(secondsLeft.value));

onMounted(() => {
  ticker = setInterval(() => {
    nowMs.value = Date.now();
    // Once, on the boundary. Repeating it would turn a rollover into a poll.
    if (secondsLeft.value === 0 && !announcedElapsed) {
      announcedElapsed = true;
      emit('elapsed');
    }
  }, 1000);
});

onBeforeUnmount(() => {
  if (ticker) clearInterval(ticker);
});

const list = computed(() => props.quests.quests);
const segments = computed(() => questSegments(list.value));
const done = computed(() => completedCount(list.value));
const unclaimed = computed(() => props.quests.unclaimed);
const featured = computed(() => featuredQuest(list.value, props.quests.dayIndex));
const state = computed(() => (featured.value ? featuredState(featured.value) : null));

/** `22 / 40 · next up`. The state is what makes the figures worth reading. */
const featuredLine = computed(() => {
  const quest = featured.value;
  if (!quest) return null;
  const figures = `${formatCredits(quest.progress)} / ${formatCredits(quest.target)}`;
  return `${figures} · ${state.value === 'ready' ? 'ready to claim' : 'next up'}`;
});

/**
 * The card's own sentence, for a screen reader and for the tooltip.
 *
 * The reward reads `+450` on screen, which is the design and is ambiguous about
 * its unit; both figures are stated here rather than crowding the plate with
 * them.
 */
const summary = computed(() => {
  const parts = [`Daily quests: ${done.value} of ${list.value.length} complete`];
  if (unclaimed.value > 0) parts.push(`${unclaimed.value} ready to claim`);
  const quest = featured.value;
  if (quest) {
    parts.push(
      `${quest.name}, ${quest.progress} of ${quest.target}, pays ` +
        `${quest.reward.credits} credits and ${quest.reward.xp} XP`,
    );
  }
  parts.push(`new set in ${resetLabel.value}`);
  return `${parts.join('. ')}.`;
});

/**
 * One glyph per metric, as path data on a 16 × 16 box.
 *
 * Stroked in `currentColor` so the icon takes the plate's own colour, and
 * expressed as arcs rather than `<circle>` so the whole set is one shape of
 * thing to render.
 */
const GLYPHS: Record<string, string[]> = {
  // A reticle: the ring, and a tick off each side.
  kills: ['M11.2 8a3.2 3.2 0 1 1-6.4 0a3.2 3.2 0 1 1 6.4 0', 'M8 1.5v2.4', 'M8 12.1v2.4', 'M1.5 8h2.4', 'M12.1 8h2.4'],
  // Chevrons, the way a rank is drawn.
  wins: ['M3 9.4 8 4.9l5 4.5', 'M3 13 8 8.5l5 4.5'],
  // A pennant on its pole.
  sorties: ['M4 2v12', 'M4 3h8.5l-2.2 2.6 2.2 2.6H4'],
  // A crate, banded.
  crates: ['M2.5 4.5h11v8h-11z', 'M2.5 7.6h11', 'M8 4.5v3.1'],
  // A four-point burst.
  damage: ['M8 1.5 9.5 6.5 14.5 8 9.5 9.5 8 14.5 6.5 9.5 1.5 8 6.5 6.5Z'],
  // Two chevrons, travelling.
  distance: ['M4 3l4.6 5-4.6 5', 'M9.6 3l3 5-3 5'],
  // A shield, ticked.
  flawless: ['M8 1.8 13 3.5v4.3c0 3.1-2.2 5.2-5 6.4-2.8-1.2-5-3.3-5-6.4V3.5z', 'M5.9 8.1 7.6 9.8l2.9-3.3'],
};

const glyph = computed(() => {
  const metric = featured.value?.metric ?? '';
  return GLYPHS[metric] ?? GLYPHS.kills;
});
</script>

<template>
  <!-- A div, not a button: the re-roll button sits inside it, and a button cannot hold one. -->
  <div
    role="button"
    tabindex="0"
    class="quest-box"
    :class="{ ready: unclaimed > 0 }"
    data-test="quest-box"
    :title="summary"
    :aria-label="summary"
    @click="emit('open')"
    @keydown.enter.self.prevent="emit('open')"
    @keydown.space.self.prevent="emit('open')"
  >
    <header>
      <span class="label">Daily quests</span>
      <span class="reset" data-test="quest-box-reset">
        <svg class="clock" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M14 8a6 6 0 1 1-12 0a6 6 0 1 1 12 0" />
          <path d="M8 4.6V8l2.4 1.6" />
        </svg>
        {{ resetLabel }}
      </span>
    </header>

    <div class="tally">
      <span class="count">
        <strong data-test="quest-box-done">{{ done }}</strong>
        <span class="of">/ {{ list.length }}</span>
      </span>
      <button
        v-if="quests.rerollable"
        type="button"
        class="reroll"
        data-test="quest-box-reroll"
        :disabled="(diamonds ?? 0) < quests.rerollCost"
        title="Swap the unfinished quests for new ones"
        aria-label="Re-roll quests"
        @click.stop="emit('reroll')"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 12a8 8 0 1 1-2.4-5.7" /><path d="M20 3.5V8h-4.5" /></svg>
        <span class="word">Re-roll</span>
        <span class="price">{{ quests.rerollCost }} <CurrencyIcon kind="diamonds" :labelled="false" /></span>
      </button>
      <span v-if="unclaimed > 0" class="standing claim" data-test="quest-box-badge">
        {{ unclaimed }} to claim
      </span>
      <span v-else class="standing">Complete</span>
    </div>

    <div class="bar" data-test="quest-box-bar">
      <span v-for="(fill, i) in segments" :key="i" class="segment">
        <i :style="{ width: `${fill * 100}%` }" />
      </span>
    </div>

    <div v-if="featured" class="featured" :class="{ ready: state === 'ready' }">
      <span class="icon" aria-hidden="true">
        <svg viewBox="0 0 16 16"><path v-for="(d, i) in glyph" :key="i" :d="d" /></svg>
      </span>
      <span class="detail">
        <span class="name" data-test="quest-box-name">{{ featured.name }}</span>
        <span class="line" data-test="quest-box-progress">{{ featuredLine }}</span>
      </span>
      <span class="reward" data-test="quest-box-reward">
        +{{ formatCredits(featured.reward.credits) }}
      </span>
    </div>
    <div v-else class="featured empty">
      <span class="detail">
        <span class="name">Every quest claimed</span>
        <span class="line">a new set in {{ resetLabel }}</span>
      </span>
    </div>
  </div>
</template>

<style scoped>
/*
 * A card that floats over the hangar, so the same dark translucent blur as
 * everything else on this tab — an opaque slab here would be the boxed layout
 * coming back one corner at a time.
 *
 * The accent bar down the left edge is a border rather than a pseudo-element, so
 * it cannot fall out of step with the card's own height.
 *
 * **24 rem is the card's own figure now.** It used to be `TankPanel`'s, stated
 * here because the two were the same column and a card an unrelated width would
 * have read as a notice floating beside the panel rather than as the top of it —
 * so the two had to move together, and this sentence existed to say so. The card
 * holds the top-left corner and the panel the right-hand column: they no longer
 * share an edge, so the pairing is dissolved and the panel may be retuned without
 * touching this. The figure itself is unchanged, having been right for the card.
 *
 * Still a `width` rather than a `max-width`: a shrink-to-fit card would change
 * width as the featured quest's name did, which is a card that resizes itself
 * once a day. `max-width: 100%` below is what keeps it inside a narrow window.
 */
.quest-box {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 24rem;
  max-width: 100%;
  padding: 0.6rem 0.75rem 0.7rem;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 5px;
  background: rgba(10, 13, 17, 0.72);
  backdrop-filter: blur(9px);
  color: inherit;
  text-align: left;
}

.quest-box {
  cursor: pointer;
}

.reroll {
  position: relative;
  display: inline-flex;
  align-items: center;
  align-self: center;
  gap: 0.3rem;
  margin-right: auto;
  margin-left: 0.6rem;
  padding: 0.15rem 0.3rem 0.15rem 0.5rem;
  border: 1px solid rgba(232, 114, 44, 0.55);
  border-radius: 3px;
  background: rgba(232, 114, 44, 0.1);
  color: #f1d9c7;
  font: inherit;
  font-size: 0.68rem;
  font-weight: 600;
  cursor: pointer;
}

.reroll svg {
  width: 0.8rem;
  height: 0.8rem;
  fill: none;
  stroke: currentColor;
  stroke-width: 2.2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.reroll .price {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  padding: 0.05rem 0.3rem;
  border-radius: 2px;
  background: rgba(0, 0, 0, 0.4);
  color: #bfe9ff;
}

.reroll:hover:not(:disabled) {
  background: var(--accent);
  color: #0b0d11;
}

.reroll:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.quest-box:hover,
.quest-box:focus-visible {
  border-color: var(--accent);
}

/*
 * Something is waiting.
 *
 * It used to light a 3px bar down the left edge, and the card carried a dimmer
 * version of that bar at rest — a mark *beside* the card, borrowed from the
 * garage bay's own "being driven" cue. Both are gone: the card is lit as a
 * whole now, the border closing round all four sides over an amber wash, which
 * is what the profile overlay's selected rail chip and the quest window's own
 * ready row wear. One state, one appearance, wherever it is shown.
 *
 * The border is 1px on every side in every state, so nothing here changes size
 * when a quest becomes claimable — which the 3px edge did, by 2px, on a card
 * whose whole job is to sit still in the corner of the garage floor.
 */
.quest-box.ready {
  border-color: var(--accent);
  background:
    linear-gradient(180deg, rgba(232, 114, 44, 0.22) 0%, rgba(232, 114, 44, 0.1) 100%),
    rgba(10, 13, 17, 0.72);
}

header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

/*
 * The title, on its own plate. It is the card's name rather than a heading over
 * a list, and the plate is what keeps it from reading as the first line of one.
 */
.label {
  padding: 0.12rem 0.35rem;
  border-radius: 3px;
  background: rgba(232, 114, 44, 0.12);
  color: var(--accent);
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.reset {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  color: var(--steel-400);
  font-size: 0.72rem;
  font-variant-numeric: tabular-nums;
}

.clock {
  width: 0.8rem;
  height: 0.8rem;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.4;
  stroke-linecap: round;
}

.tally {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
}

.count {
  display: inline-flex;
  align-items: baseline;
  gap: 0.25rem;
}

.count strong {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
  color: #eef2f7;
}

.count .of {
  color: var(--steel-400);
  font-size: 0.85rem;
}

.standing {
  color: var(--steel-400);
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.standing.claim {
  color: var(--accent);
}

/*
 * The day's four, as four tracks. `flex: 1 1 0` rather than `1 1 auto` so every
 * segment is the same width whatever it holds — the bar is a reading of the day,
 * and a segment that sized itself to its fill would say the same thing twice and
 * disagree with the counter while doing it.
 */
.bar {
  display: flex;
  gap: 0.25rem;
}

.segment {
  flex: 1 1 0;
  height: 0.32rem;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.09);
  overflow: hidden;
}

.segment i {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--accent);
}

/*
 * The featured quest, inset. Slightly lighter than the card rather than darker:
 * the card is already translucent over a lit room, so a darker inset reads as a
 * hole in it.
 */
.featured {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.045);
  min-width: 0;
}

.featured.ready {
  border-color: rgba(232, 114, 44, 0.4);
}

.icon {
  flex: none;
  display: grid;
  place-items: center;
  width: 1.65rem;
  height: 1.65rem;
  border-radius: 4px;
  background: rgba(232, 114, 44, 0.14);
  color: var(--accent);
}

.icon svg {
  width: 0.9rem;
  height: 0.9rem;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.4;
  stroke-linecap: round;
  stroke-linejoin: round;
}

/* `min-width: 0` on both, or a long quest name pushes the reward off the card. */
.detail {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 0.05rem;
  min-width: 0;
}

.name {
  font-size: 0.82rem;
  font-weight: 600;
  color: #eef2f7;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.line {
  color: var(--steel-400);
  font-size: 0.68rem;
  font-variant-numeric: tabular-nums;
}

.reward {
  flex: none;
  color: var(--accent);
  font-size: 0.78rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

/* Nothing left to point at, so no icon and no payout — just the state. */
.featured.empty .name {
  color: var(--steel-200);
  font-weight: 500;
}
</style>
